import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import reloadCaptchaImg from "../static/images/reloadCaptcha.png";
import Logo from "../common-components/Logo";
import { AppConstants } from "../constants";
import Lottie from "lottie-react";
import loginLottie from "../static/login-asset/wm-login-lottie.json";

type LoginFormInputs = {
  username: string;
  password: string;
  captcha?: string;
  rememberMe: boolean;
};

const EyeOffIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOpenIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);
const Login: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isValid, isSubmitting },
  } = useForm<LoginFormInputs>({
    mode: "onChange",
  });

  const [showCaptcha, setShowCaptcha] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [attemptsLeftMessage, setAttemptsLeftMessage] = useState(-1);
  const [maxSessionsReached, setMaxSessionsReached] = useState(false);
  const [captchaId, setCaptchaId] = useState("");
  const [captchaSrc, setCaptchaSrc] = useState("");
  const currentYear = new Date().getFullYear();

  const generateCaptchaId = () =>
    "CAPTCHA-" + Math.random().toString(36).substring(2, 10).toUpperCase();

  const initializeCaptcha = () => {
    const newId = generateCaptchaId();
    setCaptchaId(newId);
    setCaptchaSrc(`/login/Captcha.jpg?t=${Date.now()}&wm_captchaId=${newId}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasTargetProvider = params.has("target_provider");

    const checkLoginMode = async () => {
      try {
        const profileResponse = await fetch(
          window.location.origin + "/login/rest/profile",
        );
        if (profileResponse.status === 200) {
          window.location.href = window.location.origin + "/";
          return;
        }

        const formLoginResponse = await fetch(
          window.location.origin + "/login/rest/no-auth/is-form-login",
        );
        const isFormLoginEnabled = await formLoginResponse.json();


        if (!isFormLoginEnabled && !hasTargetProvider) {
          window.location.href = window.location.origin + "/";
        }
        // SSO enabled + no target_provider → redirect to home
        // SSO disabled → show login
      } catch (error) {
        console.error("Login mode check failed:", error);
      }
    };

    checkLoginMode();
  }, []);

  const onSubmit = async (data: LoginFormInputs) => {
    const encodedFormData = new URLSearchParams();
    encodedFormData.append("wm_captchaId", captchaId);
    encodedFormData.append("j_username", data.username);
    encodedFormData.append("j_password", data.password);
    encodedFormData.append("regButton", "Login");

    if (data.rememberMe) {
      encodedFormData.append("rememberMe", "yes");
    }

    if (showCaptcha) {
      encodedFormData.append("showCaptcha", "true");
      encodedFormData.append("wm_captchaAnswer", data.captcha || "");
    }

    // Detect DB login mode from query param
    const params = new URLSearchParams(window.location.search);
    const isDbLogin = params.get("target_provider") === "DB";

    // Add a delay to ensure spinner is clearly visible
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      let finalQuery = "";
      if (params.has("target_url")) {
        const rawTarget = params.get("target_url");
        if (rawTarget) {
          finalQuery = "?target_url=" + encodeURIComponent(rawTarget);
        }
      }

      const response = await fetch(
        window.location.origin + "/login/authenticate" + finalQuery,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            ...(isDbLogin && {
              "X-WM-AUTH-PROVIDER": "Platform DB",
            }),
          },
          body: encodedFormData.toString(),
          credentials: "include",
        },
      );

      if (response.url && response.status === 200) {
        window.location.href = response.url;
        return;
      }

      const outputData = await response.json();

      setErrorMessage(outputData?.errorMessage || "");
      setAttemptsLeftMessage(outputData?.attemptsLeft ?? -1);
      setMaxSessionsReached(outputData?.maxSessionsReached ?? false);
      setShowCaptcha(outputData?.showCaptcha || false);

      if (outputData?.showCaptcha) {
        initializeCaptcha();
      }
    } catch (error) {
      setErrorMessage("An error occurred during login. Please try again.");
    }

    reset({
      username: "",
      password: "",
      captcha: "",
      rememberMe: false,
    });
  };

  let emailValidationClass = "";
  if (errors.username) {
    emailValidationClass = "error";
  } else if (touchedFields.username) {
    emailValidationClass = "success";
  }

  return (
    <div className="login-container">
      <div className="row">
        <div className="login-left-section col-xs-12 col-sm-5 col-md-5 col-lg-5">
          <div className="login-content">
            <Logo />
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="signup-form form-container login"
            >
              <div className="form-title">
                <h3>Login to WaveMaker</h3>
              </div>
              <div className="form-row">
                <div className="col-md-12">
                  {errorMessage && (
                    <p className="help-block validation alert alert-danger serverError">
                      {errorMessage}
                    </p>
                  )}
                </div>
                <div className="col-md-12">
                  {attemptsLeftMessage !== -1 && (
                    <p className="help-block validation alert alert-danger serverError">
                      {attemptsLeftMessage} Attempts Left
                    </p>
                  )}
                </div>
                <div className="col-md-12">
                  {maxSessionsReached && (
                    <p className="help-block validation alert alert-danger serverError">
                      {maxSessionsReached}
                    </p>
                  )}
                </div>
                <div className="row">
                  <div
                    className={`col-sm-12 control-group no-padding ${emailValidationClass}`}
                  >
                    <input
                      className="inputText xyz"
                      type="email"
                      placeholder="Email"
                      autoComplete="username"
                      {...register("username", {
                        required: "Please enter a valid email address",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Not a valid email address",
                        },
                      })}
                      aria-invalid={errors.username ? "true" : "false"}
                    />
                    <p className="help-block validation alert alert-danger">
                      {errors.username && errors.username.message}
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div
                    className={`col-sm-12 pass-help control-group no-padding ${errors.password ? "error" : touchedFields.password ? "success" : ""}`}
                  >
                    <div style={{ position: "relative" }}>
                      <input
                        className="inputText xyz"
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        autoComplete="current-password"
                        {...register("password", {
                          required: "Password field can't be empty",
                        })}
                        aria-invalid={errors.password ? "true" : "false"}
                        style={{ paddingRight: "40px" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                        style={{
                          position: "absolute",
                          right: "16px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeOpenIcon />}
                      </button>
                    </div>
                    <p className="help-block validation alert alert-danger">
                      {errors.password && errors.password.message}
                    </p>
                  </div>
                </div>

                {showCaptcha && (
                  <div id="captchaDisplay">
                    <div className="row">
                      <div className="col-sm-12 no-padding trim-box">
                        <label className="input-caption">
                          Verification text<span className="req">*</span>
                        </label>
                        <div className="clearfix">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                              gap: "6px",
                            }}
                          >
                            <img
                              className="captchaImg col-sm-4 no-padding"
                              src={captchaSrc}
                              width="100"
                              height="50"
                              alt="Captcha"
                            />
                            <img
                              className="refreshImg pull-left"
                              src={reloadCaptchaImg}
                              alt="Reload Captcha"
                              onClick={initializeCaptcha}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                          <div
                            className={`col-sm-12 no-padding control-group ${errors.captcha ? "error" : touchedFields.captcha ? "success" : ""}`}
                          >
                            <input
                              className="inputText captchaField"
                              type="text"
                              placeholder="Enter Captcha"
                              {...register("captcha", {
                                required: "Captcha field can't be empty",
                              })}
                              aria-invalid={errors.captcha ? "true" : "false"}
                            />
                            <p className="help-block validation alert alert-danger">
                              {errors.captcha && errors.captcha.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-sm-12 no-padding">
                    <div className="custom-col">
                      <button
                        type="submit"
                        className="btn btn-primary btn-submit btn-block"
                        disabled={!isValid || isSubmitting}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isSubmitting && (
                          <svg
                            style={{
                              animation: "spin 1s linear infinite",
                              marginRight: "8px",
                              height: "20px",
                              width: "20px",
                            }}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              style={{ opacity: 0.25 }}
                            ></circle>
                            <path
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              style={{ opacity: 0.75 }}
                            ></path>
                          </svg>
                        )}
                        Login
                      </button>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12 no-padding">
                    <div className="col-sm-6 no-padding">
                      <div className="stay-signed">
                        <input type="checkbox" {...register("rememberMe")} />
                        <span>Remember Me</span>
                      </div>
                    </div>
                    <div className="col-sm-6 no-padding">
                      <div className="forgot-pass">
                        <Link to="/forgotpassword" className="pull-right">
                          Forgot Password?
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
            <div className="signup-section">
              <div className="separator">
                <span>or</span>
              </div>
              <div className="col-sm-12 no-padding">
                <p>
                  Don't have an account?{" "}
                  <a
                    href="https://www.wavemaker.com/get-demo/"
                    className="register-btn"
                  >
                    Sign Up
                  </a>
                </p>
              </div>
            </div>
          </div>
          <p className="copyright-text">
            © {currentYear} WaveMaker. All Rights Reserved
          </p>
        </div>
        <div className="login-right-section col-xs-12 col-sm-7 col-md-7 col-lg-7">
          <div className="right-content-wrapper">
            <h3 className="right-section-title">
              {AppConstants.CAROUSELTITLE}
            </h3>
            <Lottie
              animationData={loginLottie}
              loop={true}
              className="lottie-animation"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
